export const STYLE_ATTR = "data-feedback-hub-styles";

export const styles = `
.fh-root, .fh-root * { box-sizing: border-box; }
.fh-root {
  position: fixed; z-index: 2147483600;
  font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  color: #18181b;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
}
.fh-root {
  --fh-bottom: 20px;
  --fh-side: 20px;
}
.fh-pos-br { right: var(--fh-side); bottom: var(--fh-bottom); }
.fh-pos-bl { left: var(--fh-side); bottom: var(--fh-bottom); }

@media (max-width: 640px) {
  .fh-root[data-mobile-bottom] {
    --fh-bottom: var(--fh-bottom-mobile);
  }
}

@keyframes fh-panel-in {
  0%   { opacity: 0; transform: translateY(24px) scale(0.82); }
  55%  { opacity: 1; transform: translateY(-6px) scale(1.045); }
  78%  { transform: translateY(2px) scale(0.985); }
  92%  { transform: translateY(-1px) scale(1.005); }
  100% { transform: translateY(0) scale(1); }
}
@keyframes fh-spin { to { transform: rotate(360deg); } }
@keyframes fh-success-pop {
  0%   { opacity: 0; transform: scale(0.4); }
  50%  { opacity: 1; transform: scale(1.18); }
  72%  { transform: scale(0.93); }
  100% { opacity: 1; transform: scale(1); }
}
@keyframes fh-success-ring {
  0%   { opacity: 0.55; transform: scale(0.6); }
  100% { opacity: 0;    transform: scale(2.6); }
}
@keyframes fh-fab-tap {
  0%   { transform: scale(1); }
  40%  { transform: scale(0.92); }
  100% { transform: scale(1); }
}
@keyframes fh-chip-pop {
  0%   { transform: scale(1); }
  30%  { transform: scale(1.08); }
  60%  { transform: scale(0.97); }
  100% { transform: scale(1); }
}

/* ------------------------------ FAB ------------------------------ */

.fh-fab {
  appearance: none; border: 0; cursor: pointer;
  min-width: 44px; min-height: 44px;
  padding: 0 16px;
  border-radius: 999px;
  background: var(--fh-accent, #111);
  color: #fff;
  font: 500 13.5px/1 ui-sans-serif, system-ui, sans-serif;
  letter-spacing: -0.01em;
  display: inline-flex; align-items: center; gap: 8px;
  box-shadow:
    0 1px 2px rgba(0,0,0,0.08),
    0 8px 24px -4px color-mix(in srgb, var(--fh-accent, #111) 28%, transparent),
    inset 0 1px 0 rgba(255,255,255,0.12);
  transition: transform 0.16s cubic-bezier(0.4, 0, 0.2, 1),
              box-shadow 0.16s ease,
              filter 0.16s ease;
}
.fh-fab:hover {
  transform: translateY(-1px);
  filter: brightness(1.05);
  box-shadow:
    0 1px 2px rgba(0,0,0,0.1),
    0 14px 32px -6px color-mix(in srgb, var(--fh-accent, #111) 36%, transparent),
    inset 0 1px 0 rgba(255,255,255,0.12);
}
.fh-fab:active { animation: fh-fab-tap 0.22s ease-out; }
.fh-fab[aria-expanded="true"] {
  background: #18181b;
  box-shadow: 0 1px 2px rgba(0,0,0,0.08), 0 6px 20px rgba(0,0,0,0.15);
}
.fh-fab svg { display: block; }

/* Chevron — subtle right-shift on hover reinforces the "expand" affordance.
   Stays consistent in both opacity states so the shape reads clearly even
   when the FAB is at resting opacity. */
.fh-fab-chevron {
  margin-left: -2px;
  opacity: 0.9;
  transition: transform 0.16s ease, opacity 0.16s ease;
}
.fh-fab:hover .fh-fab-chevron { transform: translateX(2px); opacity: 1; }
.fh-fab[aria-expanded="true"] .fh-fab-chevron { transform: rotate(90deg); }

/* Desktop: FAB rests at low opacity so it doesn't compete with host
   content; brightens on hover/focus. Skipped on touch-only devices
   (no hover state) and when the panel is open (FAB is the close button
   then and must look solid). */
@media (hover: hover) and (pointer: fine) {
  .fh-root:not(.fh-open) .fh-fab {
    opacity: 0.55;
    transition:
      opacity 0.18s ease,
      transform 0.16s cubic-bezier(0.4, 0, 0.2, 1),
      box-shadow 0.16s ease,
      filter 0.16s ease;
  }
  .fh-root:not(.fh-open) .fh-fab:hover,
  .fh-root:not(.fh-open) .fh-fab:focus-visible { opacity: 1; }
}

/* First-visit entrance — fade up to full, hold briefly, settle to the
   resting opacity. Only added by the widget on the first ever mount per
   browser (localStorage flag). */
@keyframes fh-fab-entrance {
  0%   { opacity: 0; transform: translateY(8px); }
  24%  { opacity: 1; transform: translateY(0); }
  76%  { opacity: 1; transform: translateY(0); }
  100% { opacity: 0.55; transform: translateY(0); }
}
@media (hover: hover) and (pointer: fine) {
  .fh-root.fh-first-visit:not(.fh-open) .fh-fab {
    /* fill-mode left default ("none") on purpose — once the animation
       finishes, the FAB reverts to the static :not(.fh-open) opacity
       rule (0.55), which lets :hover override it. fill-mode: forwards
       would lock the animated value above hover. */
    animation: fh-fab-entrance 2.4s cubic-bezier(0.4, 0, 0.2, 1) 0.35s;
  }
}

/* Accessibility honors — reduced motion gets a static paint, reduced
   transparency keeps the FAB at full opacity always. */
@media (prefers-reduced-motion: reduce) {
  .fh-root.fh-first-visit:not(.fh-open) .fh-fab { animation: none; }
  .fh-fab-chevron { transition: none; }
}
@media (prefers-reduced-transparency: reduce) {
  .fh-root:not(.fh-open) .fh-fab { opacity: 1 !important; }
}

/* ─────────────────────────────────────────────────────────────────
   Mobile: shrink the FAB to a slim edge-pill so it never covers
   the host app's fixed bottom navigation. The pill sits flush with
   the right edge, vertically centered. Tap area extended via a
   pseudo-element so the 4px visual still meets WCAG 44×44 tap-target.
   ─────────────────────────────────────────────────────────────── */
@media (max-width: 640px) {
  /* Edge pill — only when the panel is closed. Open state reverts
     to the normal big FAB so the close button is reachable.
     Sits in the lower-mid of the screen and idles at ~58% opacity so
     it never competes with the host app's content; brightens on touch. */
  .fh-root:not(.fh-open) .fh-fab {
    position: fixed;
    top: 66%;
    transform: translateY(-50%);
    bottom: auto;
    min-width: 0;
    min-height: 0;
    width: 5px;
    height: 56px;
    padding: 0;
    border-radius: 6px 0 0 6px;
    font-size: 0;
    overflow: visible;
    opacity: 0.58;
    box-shadow: 0 2px 8px -1px rgba(0,0,0,0.18);
    transition:
      width 0.18s cubic-bezier(0.34, 1.56, 0.64, 1),
      height 0.18s ease,
      border-radius 0.18s ease,
      opacity 0.15s ease,
      filter 0.15s ease;
  }
  .fh-root:not(.fh-open) .fh-fab:hover { opacity: 0.85; }
  .fh-root.fh-pos-br:not(.fh-open) .fh-fab { right: 0; left: auto; }
  .fh-root.fh-pos-bl:not(.fh-open) .fh-fab { left: 0; right: auto; border-radius: 0 6px 6px 0; }

  /* Extend tap target invisibly to meet 44×44 */
  .fh-root:not(.fh-open) .fh-fab::before {
    content: "";
    position: absolute;
    inset: -10px -22px;
  }
  .fh-root:not(.fh-open) .fh-fab svg { display: none; }

  /* Press feedback — bloom the pill briefly + go fully opaque */
  .fh-root:not(.fh-open) .fh-fab:active {
    width: 9px;
    height: 64px;
    opacity: 1;
    filter: brightness(1.08);
  }

  /* A faint "incoming-edge" pulse so users notice it the first time */
  .fh-root:not(.fh-open) .fh-fab::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: inherit;
    opacity: 0.55;
    animation: fh-edge-pulse 2.8s ease-out infinite;
    pointer-events: none;
  }
  @keyframes fh-edge-pulse {
    0%   { transform: scaleX(1);  opacity: 0.55; }
    70%  { transform: scaleX(3.5); opacity: 0; }
    100% { transform: scaleX(3.5); opacity: 0; }
  }
}

/* ------------------------------ Panel ------------------------------ */

.fh-panel {
  position: absolute;
  bottom: 60px;
  width: 380px;
  max-width: calc(100vw - 32px);
  background: #fff;
  border-radius: 16px;
  /* Layered shadow: tight close-in, soft diffuse far. Subtle inner highlight. */
  box-shadow:
    0 0 0 1px rgba(0,0,0,0.04),
    0 1px 2px rgba(0,0,0,0.04),
    0 12px 24px -8px rgba(0,0,0,0.12),
    0 32px 64px -20px rgba(0,0,0,0.18),
    inset 0 1px 0 rgba(255,255,255,0.8);
  display: flex; flex-direction: column;
  overflow: hidden;
  animation: fh-panel-in 0.46s cubic-bezier(0.34, 1.56, 0.64, 1);
  transform-origin: bottom right;
}
.fh-pos-br .fh-panel { right: 0; }
.fh-pos-bl .fh-panel { left: 0; transform-origin: bottom left; }

@media (max-width: 480px) {
  .fh-panel {
    position: fixed;
    left: 0; right: 0; bottom: 0;
    width: 100vw;
    max-width: 100vw;
    border-radius: 18px 18px 0 0;
    transform-origin: bottom center;
  }
}

.fh-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 16px 4px;
}
.fh-title {
  font-size: 11px; font-weight: 600;
  letter-spacing: 0.14em; text-transform: uppercase;
  color: #71717a;
  display: inline-flex; align-items: center; gap: 7px;
}
.fh-title-dot {
  display: inline-block;
  width: 6px; height: 6px;
  border-radius: 999px;
  background: var(--fh-accent, #111);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--fh-accent, #111) 18%, transparent);
}
.fh-close {
  appearance: none; border: 0; background: transparent;
  cursor: pointer; padding: 5px; margin: -5px;
  color: #a1a1aa; line-height: 0;
  border-radius: 6px;
  transition: color .12s ease, background .12s ease;
}
.fh-close:hover { color: #18181b; background: #f4f4f5; }

/* ------------------------------ Type chips ------------------------------ */

.fh-types {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px;
  padding: 4px 16px 0;
}
.fh-type {
  position: relative;
  appearance: none; cursor: pointer;
  border: 1px solid #e6e3da;
  background: #fff;
  border-radius: 12px;
  padding: 12px 4px 10px;
  font-size: 11.5px; font-weight: 500;
  display: flex; flex-direction: column; align-items: center; gap: 6px;
  color: #52525b;
  transition: transform .15s ease, border-color .12s ease, color .12s ease, background .12s ease;
  font-family: inherit;
  overflow: hidden;
}
.fh-type::before {
  content: "";
  position: absolute; inset: 0;
  background: var(--fh-chip-tint, transparent);
  opacity: 0;
  transition: opacity 0.16s ease;
}
.fh-type:hover {
  border-color: #d4d4d8;
  color: #18181b;
  transform: translateY(-1px);
}
.fh-type[aria-pressed="true"] {
  border-color: var(--fh-chip-color, var(--fh-accent, #111));
  color: var(--fh-chip-color, var(--fh-accent, #111));
  box-shadow: inset 0 0 0 1px var(--fh-chip-color, var(--fh-accent, #111));
  animation: fh-chip-pop 0.32s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.fh-type[aria-pressed="true"]::before { opacity: 0.07; }
.fh-type > * { position: relative; }
.fh-type-glyph {
  width: 26px; height: 26px;
  display: inline-flex; align-items: center; justify-content: center;
  border: 1.5px solid currentColor;
  border-radius: 6px;
  font-family: ui-monospace, "SFMono-Regular", Menlo, monospace;
  font-size: 13px; font-weight: 600;
  background: #fff;
}

/* ------------------------------ Body / textarea ------------------------------ */

.fh-body { padding: 12px 16px 0; }
.fh-textarea {
  width: 100%;
  min-height: 100px; max-height: 240px;
  resize: none;
  border: 1px solid #e6e3da;
  border-radius: 12px;
  padding: 12px 14px;
  font: inherit;
  font-size: 14px;
  line-height: 1.55;
  outline: none;
  background: #fdfcf9;
  color: #18181b;
  transition: border-color .12s ease, background .12s ease, box-shadow .12s ease;
  font-family: inherit;
}
.fh-textarea:focus {
  border-color: var(--fh-accent, #111);
  background: #fff;
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--fh-accent, #111) 14%, transparent);
}
.fh-textarea::placeholder { color: #a1a1aa; }

/* ------------------------------ Screenshots ------------------------------ */

.fh-attach {
  margin: 8px 16px 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Thumbnail strip */
.fh-shots {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.fh-shot-item {
  position: relative;
  width: 64px; height: 48px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e4e4e7;
  background: #fafafa;
  flex: 0 0 auto;
}
.fh-shot-thumb {
  width: 100%; height: 100%;
  object-fit: cover;
  display: block;
}
.fh-shot-src {
  position: absolute;
  left: 3px; bottom: 3px;
  padding: 1px 4px;
  border-radius: 4px;
  font: 600 8.5px/1.4 inherit;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #fff;
  background: rgba(0, 0, 0, 0.55);
  pointer-events: none;
}
.fh-shot-actions {
  position: absolute;
  top: 3px; right: 3px;
  display: flex; gap: 3px;
  opacity: 0;
  transition: opacity .12s ease;
}
.fh-shot-item:hover .fh-shot-actions,
.fh-shot-item:focus-within .fh-shot-actions { opacity: 1; }
.fh-shot-act {
  appearance: none; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  width: 18px; height: 18px;
  padding: 0; border: 0;
  border-radius: 5px;
  color: #18181b;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
  transition: background .12s ease;
}
.fh-shot-act:hover:not(:disabled) { background: #fff; }
.fh-shot-act:disabled { opacity: 0.5; cursor: not-allowed; }
.fh-shot-act svg { display: block; }

/* Add-source row */
.fh-add-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.fh-add-btn {
  appearance: none; cursor: pointer;
  background: #fff;
  border: 1px dashed #d4d4d8;
  border-radius: 9px;
  padding: 7px 10px;
  font: 500 11.5px/1.2 inherit;
  color: #52525b;
  display: inline-flex; align-items: center; gap: 6px;
  transition: border-color .12s ease, color .12s ease, background .12s ease;
}
.fh-add-btn:hover:not(:disabled) {
  border-color: var(--fh-accent, #18181b);
  color: var(--fh-accent, #18181b);
  background: color-mix(in srgb, var(--fh-accent, #18181b) 4%, #fff);
}
.fh-add-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.fh-add-btn svg { display: block; }

.fh-attach-note {
  font-size: 11px;
  color: #71717a;
  letter-spacing: -0.005em;
}

/* ------------------------------ Provenance pill ------------------------------ */

.fh-prov {
  margin: 8px 16px 0;
  display: flex; align-items: center; gap: 6px;
  padding: 6px 9px;
  border: 1px dashed #e6e3da;
  border-radius: 8px;
  font-family: ui-monospace, "SFMono-Regular", Menlo, monospace;
  font-size: 10.5px;
  color: #a1a1aa;
  background: #fafafa;
  overflow: hidden;
}
.fh-prov-icon { color: #d4d4d8; flex: 0 0 auto; }
.fh-prov-host {
  color: #52525b;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  min-width: 0;
}
.fh-prov-sep { color: #d4d4d8; }
.fh-prov-vp { color: #71717a; white-space: nowrap; flex: 0 0 auto; }

/* ------------------------------ Footer ------------------------------ */

.fh-footer {
  display: flex; align-items: center; justify-content: space-between;
  gap: 8px;
  padding: 10px 16px 14px;
}
.fh-count {
  font-family: ui-monospace, "SFMono-Regular", Menlo, monospace;
  font-size: 10.5px;
  color: #a1a1aa;
  letter-spacing: 0.04em;
  font-variant-numeric: tabular-nums;
  transition: color .12s ease;
}
.fh-count[data-over="true"] { color: #dc2626; }
.fh-count[data-active="true"] { color: #71717a; }

.fh-submit-wrap {
  display: inline-flex; align-items: center; gap: 8px;
}
.fh-kbd-hint {
  display: inline-flex; align-items: center; gap: 3px;
  font-family: ui-monospace, "SFMono-Regular", Menlo, monospace;
  font-size: 10px;
  color: #a1a1aa;
}
.fh-kbd-hint kbd {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 16px; height: 16px;
  padding: 0 4px;
  border: 1px solid #e6e3da;
  border-bottom-width: 1.5px;
  border-radius: 4px;
  background: #fff;
  font-family: inherit;
  font-size: 10px;
  color: #71717a;
  line-height: 1;
}
.fh-submit {
  appearance: none; border: 0; cursor: pointer;
  background: var(--fh-accent, #111);
  color: #fff;
  font: 500 12.5px/1 inherit;
  letter-spacing: -0.005em;
  padding: 9px 14px;
  border-radius: 9px;
  display: inline-flex; align-items: center; gap: 7px;
  box-shadow:
    0 1px 2px rgba(0,0,0,0.05),
    inset 0 1px 0 rgba(255,255,255,0.12);
  transition: filter .12s ease, transform .1s ease, box-shadow .12s ease;
}
.fh-submit:hover:not(:disabled) { filter: brightness(1.08); }
.fh-submit:active:not(:disabled) { transform: translateY(1px); }
.fh-submit:disabled { background: #d4d4d8; cursor: not-allowed; box-shadow: none; }
.fh-submit-spin {
  width: 12px; height: 12px;
  border: 1.5px solid currentColor;
  border-top-color: transparent;
  border-radius: 999px;
  animation: fh-spin 0.7s linear infinite;
}
.fh-submit-arrow {
  margin-left: 1px;
  opacity: 0.7;
  transition: transform .15s ease, opacity .15s ease;
}
.fh-submit:hover:not(:disabled) .fh-submit-arrow {
  transform: translateX(2px);
  opacity: 1;
}

/* ------------------------------ Error ------------------------------ */

.fh-error {
  margin: 0 16px 12px;
  font-size: 12px;
  color: #b91c1c;
  background: #fef2f2;
  border: 1px solid #fecaca;
  padding: 8px 10px;
  border-radius: 8px;
  display: flex; align-items: flex-start; gap: 7px;
}
.fh-error-dot {
  flex: 0 0 auto;
  width: 6px; height: 6px;
  border-radius: 999px;
  background: #ef4444;
  margin-top: 5px;
}

/* ------------------------------ Success ------------------------------ */

.fh-success {
  padding: 36px 24px 32px;
  text-align: center;
  display: flex; flex-direction: column; align-items: center; gap: 12px;
}
.fh-success-mark {
  position: relative;
  width: 56px; height: 56px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--fh-accent, #111) 12%, #fff);
  color: var(--fh-accent, #111);
  display: inline-flex; align-items: center; justify-content: center;
  animation: fh-success-pop 0.36s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.fh-success-mark::after {
  content: "";
  position: absolute; inset: -2px;
  border-radius: inherit;
  border: 2px solid color-mix(in srgb, var(--fh-accent, #111) 60%, transparent);
  animation: fh-success-ring 0.7s ease-out forwards;
  animation-delay: 0.08s;
  opacity: 0;
}
.fh-success-title { font-size: 15px; font-weight: 500; color: #18181b; letter-spacing: -0.01em; }
.fh-success-sub {
  font-size: 12px; color: #71717a;
  display: inline-flex; align-items: center; gap: 6px;
}
.fh-success-stat {
  font-family: ui-monospace, "SFMono-Regular", Menlo, monospace;
  font-size: 10.5px;
  color: #a1a1aa;
  letter-spacing: 0.04em;
  margin-top: 2px;
}
.fh-success-track {
  display: flex; align-items: center; gap: 6px;
  margin-top: 10px;
  padding: 6px 8px;
  border: 1px solid #e4e4e7;
  border-radius: 6px;
  background: #fafafa;
  max-width: 100%;
}
.fh-success-track-link {
  font-family: ui-monospace, "SFMono-Regular", Menlo, monospace;
  font-size: 11.5px;
  color: #3f3f46;
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1 1 auto;
  min-width: 0;
}
.fh-success-track-link:hover { color: #18181b; text-decoration: underline; }
.fh-success-track-copy {
  appearance: none; border: 0; cursor: pointer;
  background: #18181b; color: #fff;
  font: 500 11px ui-sans-serif, system-ui, sans-serif;
  padding: 4px 8px;
  border-radius: 4px;
  flex: 0 0 auto;
  transition: background 0.12s ease;
}
.fh-success-track-copy:hover { background: #3f3f46; }

/* ------------------------------ Screenshot Editor (full-screen) ------------------------------ */

.fh-editor {
  position: fixed;
  top: 0; left: 0; right: 0;
  /* Dynamic viewport height on mobile so the URL bar showing/hiding
     doesn't shove the bottom off-screen. Falls back to 100vh. */
  height: 100vh;
  height: 100dvh;
  z-index: 2147483647;
  background: rgba(15, 15, 17, 0.92);
  backdrop-filter: blur(8px);
  display: flex;
  flex-direction: column;
  font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  color: #f4f4f5;
  animation: fh-panel-in 0.16s ease-out;
}
.fh-editor-toolbar {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255,255,255,0.08);
  background: rgba(0,0,0,0.35);
}
.fh-editor-tools {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px;
  background: rgba(255,255,255,0.06);
  border-radius: 10px;
}
.fh-editor-tool {
  appearance: none; cursor: pointer;
  border: 0; background: transparent;
  color: rgba(255,255,255,0.7);
  font: 500 12.5px/1 inherit;
  display: inline-flex; align-items: center; gap: 6px;
  padding: 7px 11px;
  border-radius: 7px;
  transition: background 0.12s, color 0.12s;
}
.fh-editor-tool:hover { color: #fff; background: rgba(255,255,255,0.06); }
.fh-editor-tool[aria-pressed="true"] {
  background: #fff;
  color: #18181b;
}

.fh-editor-swatches {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px;
  background: rgba(255,255,255,0.06);
  border-radius: 10px;
}
.fh-editor-swatch {
  appearance: none; cursor: pointer;
  width: 18px; height: 18px;
  border-radius: 999px;
  border: 1px solid rgba(255,255,255,0.18);
  padding: 0;
  box-shadow: inset 0 0 0 2px rgba(0,0,0,0);
  transition: transform 0.1s ease, box-shadow 0.12s ease, border-color 0.12s ease;
}
.fh-editor-swatch:hover { transform: scale(1.08); border-color: rgba(255,255,255,0.35); }
.fh-editor-swatch[data-active="true"] {
  border-color: #fff;
  box-shadow: inset 0 0 0 2px #18181b, 0 0 0 2px #fff;
}

.fh-editor-actions {
  display: inline-flex; align-items: center; gap: 6px;
}
.fh-editor-secondary {
  appearance: none; cursor: pointer;
  border: 1px solid rgba(255,255,255,0.12);
  background: transparent;
  color: rgba(255,255,255,0.75);
  font: 500 12px/1 inherit;
  padding: 7px 10px;
  border-radius: 7px;
  transition: background 0.12s, color 0.12s, border-color 0.12s;
}
.fh-editor-secondary:hover:not(:disabled) {
  background: rgba(255,255,255,0.06);
  color: #fff;
}
.fh-editor-secondary:disabled { opacity: 0.4; cursor: not-allowed; }
.fh-editor-cancel {
  appearance: none; cursor: pointer; border: 0;
  background: transparent;
  color: rgba(255,255,255,0.7);
  font: 500 12.5px/1 inherit;
  padding: 7px 12px;
  border-radius: 7px;
  transition: color 0.12s, background 0.12s;
}
.fh-editor-cancel:hover { color: #fff; background: rgba(255,255,255,0.06); }
.fh-editor-done {
  appearance: none; cursor: pointer; border: 0;
  background: #d6402a;
  color: #fff;
  font: 500 12.5px/1 inherit;
  padding: 8px 14px;
  border-radius: 7px;
  margin-left: 4px;
  transition: filter 0.12s, transform 0.1s;
}
.fh-editor-done:hover:not(:disabled) { filter: brightness(1.08); }
.fh-editor-done:active:not(:disabled) { transform: translateY(1px); }
.fh-editor-done:disabled { opacity: 0.55; cursor: not-allowed; }

.fh-editor-stage {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  min-height: 0;     /* allow flex child to shrink so the image fits */
  min-width: 0;
  overflow: hidden;
}
.fh-editor-canvas {
  position: relative;
  max-width: 100%;
  max-height: 100%;
  user-select: none;
  touch-action: none;
  display: block;
  /* Decorative outline that doesn't take layout space (a real border would
     shift the overlay's coordinate origin by 1px, which compounds when the
     image is scaled down to fit the editor). */
  outline: 1px solid rgba(255,255,255,0.08);
  outline-offset: -1px;
  border-radius: 4px;
  background: #fff;
  box-shadow: 0 24px 60px -10px rgba(0,0,0,0.6);
  overflow: hidden;
  font-size: 0; /* eliminates inline-image baseline whitespace */
  line-height: 0;
}
.fh-editor-img {
  display: block;
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  pointer-events: none;
}
/* Wider cap on desktop so a 2K screenshot doesn't get displayed huge. */
@media (min-width: 641px) {
  .fh-editor-canvas { max-width: min(100%, 1400px); }
}
.fh-editor-overlay {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.fh-editor-help {
  text-align: center;
  padding: 10px 16px 14px;
  font-size: 12.5px;
  color: rgba(255,255,255,0.65);
}
.fh-editor-help-keys {
  margin-left: 8px;
  color: rgba(255,255,255,0.45);
}
.fh-capture-loader {
  display: flex; flex-direction: column; align-items: center; gap: 14px;
  color: rgba(255,255,255,0.85);
  text-align: center;
}
.fh-capture-spin {
  width: 32px; height: 32px;
  border: 2px solid rgba(255,255,255,0.15);
  border-top-color: rgba(255,255,255,0.85);
  border-radius: 999px;
  animation: fh-spin 0.85s linear infinite;
}
.fh-capture-label { font-size: 15px; font-weight: 500; letter-spacing: -0.01em; }
.fh-capture-sub   { font-size: 12.5px; color: rgba(255,255,255,0.55); max-width: 28ch; }

.fh-editor-help kbd {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 18px; height: 18px;
  padding: 0 5px;
  margin: 0 1px;
  border: 1px solid rgba(255,255,255,0.18);
  border-bottom-width: 1.5px;
  border-radius: 4px;
  background: rgba(255,255,255,0.05);
  color: rgba(255,255,255,0.8);
  font-family: ui-monospace, "SFMono-Regular", Menlo, monospace;
  font-size: 10.5px;
  line-height: 1;
}

@media (max-width: 640px) {
  /* Keep the toolbar a single row on mobile — letting it wrap stole
     ~100px of stage height that the image needed to render in full. */
  .fh-editor-toolbar {
    flex-wrap: wrap;
    gap: 8px;
    padding: 8px 10px;
  }
  .fh-editor-tools { flex-wrap: wrap; gap: 2px; }
  .fh-editor-tool { padding: 6px 8px; font-size: 11.5px; }
  .fh-editor-actions { gap: 4px; }
  .fh-editor-secondary,
  .fh-editor-cancel,
  .fh-editor-done { padding: 6px 9px; font-size: 11.5px; }
  /* The keyboard-hint strip is desktop-only; mobile users tap and don't see kbd. */
  .fh-editor-help { display: none; }
  .fh-editor-stage { padding: 8px; }
}

/* ------------------------------ Foot strip ------------------------------ */

.fh-foot {
  border-top: 1px solid #f0ede3;
  padding: 8px 16px;
  font-family: ui-monospace, "SFMono-Regular", Menlo, monospace;
  font-size: 10px;
  letter-spacing: 0.06em;
  color: #a1a1aa;
  display: flex; justify-content: space-between; align-items: center;
}
.fh-foot a { color: inherit; text-decoration: none; }
.fh-foot a:hover { color: #18181b; }
.fh-foot-ver { color: #c4c4cc; cursor: default; }
.fh-foot-ver::before { content: "·"; margin: 0 6px; color: #d6d3c8; }
`;

export function injectStyles(doc: Document) {
  if (doc.querySelector(`style[${STYLE_ATTR}]`)) return;
  const el = doc.createElement("style");
  el.setAttribute(STYLE_ATTR, "");
  el.textContent = styles;
  doc.head.appendChild(el);
}
